import { ReactNode } from "react";
import Logo from "./Logo";

interface ScreenHeaderProps {
  /** Small uppercase label above title (e.g. "Today, Tuesday") */
  eyebrow?: string;
  /** Big serif headline */
  title: ReactNode;
  /** Italic supporting line (e.g. scripture) */
  subtitle?: ReactNode;
  /** Optional content rendered inside the gradient block (chips, day picker) */
  children?: ReactNode;
  /** "forest" (deep green) or "cream" (light cream w/ forest type) */
  variant?: "forest" | "cream";
  /** Show MNF logo monogram top-left */
  showLogo?: boolean;
  /** Extra trailing content (right side, e.g. avatar) */
  trailing?: ReactNode;
}

export default function ScreenHeader({
  eyebrow,
  title,
  subtitle,
  children,
  variant = "cream",
  showLogo = true,
  trailing,
}: ScreenHeaderProps) {
  const isForest = variant === "forest";

  return (
    <header
      className="relative overflow-hidden"
      style={{
        background: isForest
          ? "linear-gradient(165deg, #1E4A35 0%, #163828 100%)"
          : "transparent",
        color: isForest ? "#F5F0E8" : "var(--mnf-tx)",
        padding: "16px 22px 24px",
        borderRadius: isForest ? "0 0 28px 28px" : 0,
      }}
    >
      {isForest && <div className="mnf-orb" />}
      {isForest && (
        <div
          className="mnf-orb"
          style={{
            top: "auto",
            bottom: -90,
            left: -60,
            right: "auto",
            background: "rgba(168,212,184,0.18)",
          }}
        />
      )}

      <div className="relative flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {showLogo && (
            <Logo size={22} className={isForest ? "text-[#F5F0E8]" : ""} />
          )}
          <span
            className="mnf-serif italic"
            style={{
              fontSize: 17,
              letterSpacing: "0.04em",
              color: isForest ? "#F5F0E8" : "var(--mnf-f)",
              fontWeight: 500,
            }}
          >
            Move Nourish Flourish
          </span>
        </div>
        <div className="flex items-center gap-2">
          {trailing}
        </div>
      </div>

      {eyebrow && (
        <p
          className="mnf-eyebrow mb-2"
          style={{ color: isForest ? "rgba(229,185,151,0.95)" : "var(--mnf-t)" }}
        >
          {eyebrow}
        </p>
      )}
      <h1
        className="mnf-serif leading-[1.05]"
        style={{
          fontSize: 36,
          fontWeight: 500,
          color: isForest ? "#F5F0E8" : "var(--mnf-fd)",
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </h1>
      {subtitle && (
        <p
          className="mnf-body italic mt-2"
          style={{
            fontSize: 13.5,
            lineHeight: 1.55,
            color: isForest ? "rgba(245,240,232,0.85)" : "var(--mnf-tg)",
            maxWidth: 320,
          }}
        >
          {subtitle}
        </p>
      )}

      {children && <div className="relative mt-4">{children}</div>}
    </header>
  );
}
